import React from 'react'
import {colors} from '../../../colors'
import * as icons from './svg'

/**
 * Common properties for defining icons
 */
export interface IconProps {
  type: string
  height?: string // The height of the svg element
  width?: string // The width of the svg element
  fill?: string // Define the color used to paint the svg element
}

export interface SharedIconProps {
  icon: IconPalette | SmallIconPalette // The Icon component to use
  height?: string // The height of the svg element
  width?: string // The width of the svg element
  fill?: string // Define the color used to paint the svg element
}

// Enum value corresponds to the name in Figma
export enum IconPalette {
  AddItem = 'icon_add_item',
  Alarm = 'icon_alarm',
  AlarmClock = 'icon_alarm_clock',
  AlertTriangle = 'icon_alert_triangle',
  Audit = 'icon_audit',
  Barcode = 'icon_barcode',
  Chain = 'icon_chain',
  Checklist = 'icon_checklist',
  Cleaning = 'icon_cleaning',
  CompletedCycle = 'icon_completed_cycle',
  Confirmation = 'icon_confirmation',
  Cooking = 'icon_cooking',
  Cooler = 'icon_cooler',
  Cooling = 'icon_cooling',
  Delivery = 'icon_delivery',
  Disconnected = 'icon_disconnected',
  Dishwasher = 'icon_dishwasher',
  Document = 'icon_document',
  DocumentLinked = 'icon_document_linked',
  DragItem = 'icon_drag_item',
  Folder = 'icon_folder',
  FolderLinked = 'icon_folder_linked',
  Freezer = 'icon_freezer',
  Fridge = 'icon_fridge',
  Gateway = 'icon_gateway',
  HandheldAtp = 'icon_handheld_atp',
  HandheldProbe = 'icon_handheld_probe',
  Heating = 'icon_heating',
  Humidity = 'icon_humidity',
  IconList = 'icon_icon_list',
  InfoDisplay = 'icon_info_display',
  Kettle = 'icon_kettle',
  Late = 'icon_late',
  ListSingle = 'icon_list_single',
  Location = 'icon_location',
  Maintenance = 'icon_maintenance',
  Manual = 'icon_manual',
  NewFolder = 'icon_new_folder',
  Oven = 'icon_oven',
  Packaging = 'icon_packaging',
  Power = 'icon_power',
  Profile = 'icon_profile',
  Quality = 'icon_quality',
  Receiving = 'icon_receiving',
  Repeat = 'icon_repeat',
  Rinse = 'icon_rinse',
  Scale = 'icon_scale',
  Sensor = 'icon_sensor',
  SensorMachine = 'icon_sensor_machine',
  Service = 'icon_service',
  ServingTemp = 'icon_serving_temp',
  Settings = 'icon_settings',
  SurfaceHygiene = 'icon_surfacehygiene',
  TaskList = 'icon_task_list',
  Temperature = 'icon_temperature',
  TemperatureCold = 'icon_temperature_cold',
  Users = 'icon_users',
  Warm = 'icon_warm',
  Wash = 'icon_wash',
  Waste = 'icon_waste',
  WasteLevel = 'icon_waste_level'
}

export enum SmallIconPalette {
  ArrowheadDown = 'arrowhead_down_small',
  ArrowheadLeft = 'arrowhead_left_small',
  ArrowheadRight = 'arrowhead_right_small',
  ArrowheadUp = 'arrowhead_up_small',
  Check = 'check',
  Checkedbox = 'checked_box',
  Close = 'close_small',
  ExternalLink = 'external_link',
  Info = 'info',
  LateTask = 'late_task',
  Photo = 'photo',
  Tip = 'tip'
}

export const SharedIcon = (props: SharedIconProps) => {
  const availableIcons = {
    [IconPalette.AddItem]: icons.IconAddItem(props),
    [IconPalette.Alarm]: icons.IconAlarm(props),
    [IconPalette.AlarmClock]: icons.IconAlarmClock(props),
    [IconPalette.AlertTriangle]: icons.IconAlertTriangle(props),
    [IconPalette.Audit]: icons.IconAudit(props),
    [IconPalette.Barcode]: icons.IconBarcode(props),
    [IconPalette.Chain]: icons.IconChain(props),
    [IconPalette.Checklist]: icons.IconChecklist(props),
    [IconPalette.Cleaning]: icons.IconCleaning(props),
    [IconPalette.CompletedCycle]: icons.IconCompletedCycle(props),
    [IconPalette.Confirmation]: icons.IconConfirmation(props),
    [IconPalette.Cooking]: icons.IconCooking(props),
    [IconPalette.Cooler]: icons.IconCooler(props),
    [IconPalette.Cooling]: icons.IconCooling(props),
    [IconPalette.Delivery]: icons.IconDelivery(props),
    [IconPalette.Disconnected]: icons.IconDisconnected(props),
    [IconPalette.Dishwasher]: icons.IconDishwasher(props),
    [IconPalette.Document]: icons.IconDocument(props),
    [IconPalette.DocumentLinked]: icons.IconDocumentLinked(props),
    [IconPalette.DragItem]: icons.IconDragItem(props),
    [IconPalette.Folder]: icons.IconFolder(props),
    [IconPalette.FolderLinked]: icons.IconFolderLinked(props),
    [IconPalette.Freezer]: icons.IconFreezer(props),
    [IconPalette.Fridge]: icons.IconFridge(props),
    [IconPalette.Gateway]: icons.IconGateway(props),
    [IconPalette.HandheldAtp]: icons.IconHandheldAtp(props),
    [IconPalette.HandheldProbe]: icons.IconHandheldProbe(props),
    [IconPalette.Heating]: icons.IconHeating(props),
    [IconPalette.Humidity]: icons.IconHumidity(props),
    [IconPalette.IconList]: icons.IconIconList(props),
    [IconPalette.InfoDisplay]: icons.IconInfoDisplay(props),
    [IconPalette.Kettle]: icons.IconKettle(props),
    [IconPalette.Late]: icons.IconLate(props),
    [IconPalette.ListSingle]: icons.IconListSingle(props),
    [IconPalette.Location]: icons.IconLocation(props),
    [IconPalette.Maintenance]: icons.IconMaintenance(props),
    [IconPalette.Manual]: icons.IconManual(props),
    [IconPalette.NewFolder]: icons.IconNewFolder(props),
    [IconPalette.Oven]: icons.IconOven(props),
    [IconPalette.Packaging]: icons.IconPackaging(props),
    [IconPalette.Power]: icons.IconPower(props),
    [IconPalette.Profile]: icons.IconProfile(props),
    [IconPalette.Quality]: icons.IconQuality(props),
    [IconPalette.Receiving]: icons.IconReceiving(props),
    [IconPalette.Repeat]: icons.IconRepeat(props),
    [IconPalette.Rinse]: icons.IconRinse(props),
    [IconPalette.Scale]: icons.IconScale(props),
    [IconPalette.Sensor]: icons.IconSensor(props),
    [IconPalette.SensorMachine]: icons.IconSensorMachine(props),
    [IconPalette.Service]: icons.IconService(props),
    [IconPalette.ServingTemp]: icons.IconServingTemp(props),
    [IconPalette.Settings]: icons.IconSettings(props),
    [IconPalette.SurfaceHygiene]: icons.IconSurfaceHygiene(props),
    [IconPalette.TaskList]: icons.IconTaskList(props),
    [IconPalette.Temperature]: icons.IconTemperature(props),
    [IconPalette.TemperatureCold]: icons.IconTemperatureCold(props),
    [IconPalette.Users]: icons.IconUsers(props),
    [IconPalette.Warm]: icons.IconWarm(props),
    [IconPalette.Wash]: icons.IconWash(props),
    [IconPalette.Waste]: icons.IconWaste(props),
    [IconPalette.WasteLevel]: icons.IconWasteLevel(props),
    [SmallIconPalette.ArrowheadDown]: icons.IconArrowheadDownSmall(props),
    [SmallIconPalette.ArrowheadLeft]: icons.IconArrowheadLeftSmall(props),
    [SmallIconPalette.ArrowheadRight]: icons.IconArrowheadRightSmall(props),
    [SmallIconPalette.ArrowheadUp]: icons.IconArrowheadUpSmall(props),
    [SmallIconPalette.Check]: icons.IconCheck(props),
    [SmallIconPalette.Checkedbox]: icons.IconCheckedbox(props),
    [SmallIconPalette.Close]: icons.IconCloseSmall(props),
    [SmallIconPalette.ExternalLink]: icons.IconExternalLink(props),
    [SmallIconPalette.Info]: icons.IconInfo(props),
    [SmallIconPalette.LateTask]: icons.IconLateTask(props),
    [SmallIconPalette.Photo]: icons.IconPhoto(props),
    [SmallIconPalette.Tip]: icons.IconTip(props)
  }
  return availableIcons[props.icon]
}

/**
 * @deprecated Use SharedIcon instead going forward
 */
export const Icon = (props: IconProps) => {
  const {type, fill, ...rest} = props
  const fillOrDefault = fill || colors.brand.cobalt
  switch (type) {
    case 'home':
      return (
        <svg width="21" height="20" viewBox="0 0 21 20" {...props}>
          <path d="M10.4099 1.18372L19.25 6.31247V18.7501H12.8611V11.5267H8.13889V18.7501H1.75V6.31502L10.4099 1.18372ZM10.4143 2.14972L2.58333 6.78988V17.9167H7.30556V10.6934H13.6944V17.9167H18.4167V6.79242L10.4143 2.14972Z" />
        </svg>
      )
    case 'home-reverse':
      return (
        <svg width="20" height="20" viewBox="0 0 20 20" {...props}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12.7777 18.3333H18.3333V6.55235L9.91205 1.66663L1.66663 6.55235V18.3333H7.22218V11.11H12.7777V18.3333Z"
            fill="white"
          />
        </svg>
      )
    case 'tasks':
      return (
        <svg width="21" height="20" viewBox="0 0 21 20" {...props}>
          <path d="M7.05598 3.33337L4.68769 5.59129L3.67057 4.62158L3.00244 5.25857L4.68769 6.86527L7.72412 3.97037L7.05598 3.33337ZM9.38794 5.49876H18.8336V4.59792H9.38794V5.49876ZM18.8336 10.6805H9.38794V9.7797H18.8336V10.6805ZM7.05354 8.49441L4.68525 10.7523L3.66813 9.78261L3 10.4196L4.68525 12.0263L7.72167 9.1314L7.05354 8.49441ZM18.8336 15.8604H9.38794V14.9596H18.8336V15.8604ZM7.05354 13.6555L4.68525 15.9135L3.66813 14.9437L3 15.5807L4.68525 17.1874L7.72167 14.2925L7.05354 13.6555Z" />
        </svg>
      )
    case 'tasks-reverse':
      return (
        <svg width="21" height="20" viewBox="0 0 21 20" {...props}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M2.16675 3.66663C2.16675 2.56206 3.06218 1.66663 4.16675 1.66663H16.8334C17.938 1.66663 18.8334 2.56206 18.8334 3.66663V16.3333C18.8334 17.4379 17.938 18.3333 16.8334 18.3333H4.16675C3.06218 18.3333 2.16675 17.4379 2.16675 16.3333V3.66663ZM17.1667 5.99019H8.99088V5.23159H17.1667V5.99019ZM7.10866 4.16663L5.19635 6.06802L4.37508 5.25143L3.83559 5.78784L5.19635 7.14085L7.64815 4.70304L7.10866 4.16663ZM17.1667 10.3537H8.99088V9.59509H17.1667V10.3537ZM7.10648 8.5127L5.19418 10.4141L4.3729 9.5975L3.83341 10.1339L5.19418 11.4869L7.64597 9.04911L7.10648 8.5127ZM17.1667 14.7157H8.99088V13.9571H17.1667V14.7157ZM7.10648 12.859L5.19418 14.7604L4.3729 13.9438L3.83341 14.4802L5.19418 15.8332L7.64597 13.3954L7.10648 12.859Z"
            fill="white"
          />
        </svg>
      )
    case 'alarms':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" {...rest}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12 3C7.03677 3 3 7.0377 3 12C3 16.9632 7.03677 21 12 21C16.9632 21 21 16.9632 21 12C21 7.0377 16.9632 3 12 3ZM12 3.93206C16.4562 3.93206 20.0679 7.54381 20.0679 12C20.0679 16.4562 16.4562 20.0679 12 20.0679C7.54381 20.0679 3.93206 16.4562 3.93206 12C3.93206 7.54381 7.54381 3.93206 12 3.93206ZM11.55 14.7V7.5H12.45V14.7H11.55ZM11.55 15.6V17.4H12.45V15.6H11.55Z"
            fill={fillOrDefault}
          />
        </svg>
      )
    case 'alarms-reverse':
      return (
        <svg width="24" height="24" viewBox="0 0 25 24" fill={fillOrDefault} {...rest}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12.5 3C7.53676 3 3.5 7.0377 3.5 12C3.5 16.9632 7.53676 21 12.5 21C17.4632 21 21.5 16.9632 21.5 12C21.5 7.0377 17.4632 3 12.5 3ZM12.05 13.458V6.6H12.95V13.458H12.05ZM12.05 15.3107V17.297H12.95V15.3107H12.05Z"
            fill="white"
          />
        </svg>
      )
    case 'appliances':
      return (
        <svg width="21" height="20" viewBox="0 0 21 20" {...props}>
          <path
            fillRule="evenodd"
            d="M6.33341 2.49996H15.1213C15.5815 2.49996 15.9546 2.87306 15.9546 3.33329V6.13632H5.50008V3.33329C5.50008 2.87306 5.87318 2.49996 6.33341 2.49996ZM4.66675 3.33329C4.66675 2.41282 5.41294 1.66663 6.33341 1.66663H15.1213C16.0418 1.66663 16.788 2.41282 16.788 3.33329V6.96966H4.66675V3.33329ZM5.50008 8.56055H15.9546V16.6666C15.9546 17.1268 15.5815 17.4999 15.1213 17.4999H6.33342C5.87318 17.4999 5.50008 17.1268 5.50008 16.6666V8.56055ZM4.66675 16.6666V7.72721H16.788V16.6666C16.788 17.5871 16.0418 18.3333 15.1213 18.3333H6.33342C5.41294 18.3333 4.66675 17.5871 4.66675 16.6666ZM7.58341 5.41663H6.75008V3.33329H7.58341V5.41663ZM6.75008 15.8333H7.58341V9.16663H6.75008V15.8333Z"
          />
        </svg>
      )
    case 'appliances-reverse':
      return (
        <svg width="21" height="20" viewBox="0 0 21 20" {...props}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M6.33341 1.66663C5.41294 1.66663 4.66675 2.41282 4.66675 3.33329V6.96966H16.788V3.33329C16.788 2.41282 16.0418 1.66663 15.1213 1.66663H6.33341ZM7.58341 5.41663H6.75008V3.33329H7.58341V5.41663ZM4.66675 7.72721V16.6666C4.66675 17.5871 5.41294 18.3333 6.33342 18.3333H15.1213C16.0418 18.3333 16.788 17.5871 16.788 16.6666V7.72721H4.66675ZM7.58341 15.8333H6.75008V9.16663H7.58341V15.8333Z"
            fill="white"
          />
        </svg>
      )
    case 'documents':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" {...props}>
          <path d="M16.16 2.84H2.84v18.31h18.31v-13l-4.99-5.31zm.28 1.76l3.06 3.24h-3.06V4.6zM3.84 20.16V3.84h11.6v5h4.71v11.31H3.84z" />
          <path d="M6.96 7.24h5v1h-5zM6.95 11.57h10v1h-10zM6.95 15.9h10v1h-10z" />
        </svg>
      )
    case 'documents-reverse':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" {...props}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M15.5986 8.19807V3H3V20.9995H20.9995V8.69807H16.0986H15.5986V8.19807ZM16.5986 7.69807V3.5306L20.5285 7.69807H16.5986ZM6.75488 8.071H11.9503V7.071H6.75488V8.071ZM17.1481 12.5711H6.75098V11.5711H17.1481V12.5711ZM6.75098 17.0711H17.1481V16.0711H6.75098V17.0711Z"
            fill="white"
          />
        </svg>
      )
    case 'settings':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...rest}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M13.6884 3.00018H10.0095L9.61406 5.42122C8.68829 5.72796 7.84601 6.21886 7.13318 6.85274L4.84 5.98519L3 9.17105L4.89955 10.7255C4.80366 11.1847 4.74847 11.6622 4.74847 12.1562C4.74847 12.6501 4.80366 13.1276 4.89955 13.5869L3 15.1413L4.83991 18.327L7.13309 17.4605C7.84615 18.0945 8.68851 18.5847 9.61404 18.8919L10.0095 21.3122H13.6884L14.0839 18.892C15.0103 18.5847 15.8522 18.0944 16.5657 17.4605L18.859 18.327L20.699 15.1412L18.7988 13.5871C18.8953 13.1276 18.9505 12.65 18.9505 12.1562C18.9505 11.6623 18.8953 11.1848 18.7988 10.7253L20.699 9.17115L18.859 5.98519L16.5656 6.8528C15.8523 6.21898 15.0105 5.72795 14.0839 5.42114L13.6884 3.00018ZM10.4996 6.20309L10.8594 4.00018H12.8385L13.1984 6.20318L13.505 6.28948C14.4965 6.56854 15.3855 7.0897 16.1038 7.78855L16.3318 8.01041L18.418 7.22118L19.4079 8.93522L17.6784 10.3498L17.7569 10.6584C17.8798 11.1418 17.9505 11.6413 17.9505 12.1562C17.9505 12.6711 17.8798 13.1705 17.7569 13.6539L17.6784 13.9626L19.4079 15.3772L18.4179 17.0913L16.3317 16.3031L16.1038 16.5248C15.3857 17.2235 14.4967 17.7439 13.5046 18.024L13.1983 18.1105L12.8385 20.3122H10.8594L10.4996 18.1106L10.1935 18.024C9.20217 17.7438 8.3122 17.2234 7.59538 16.525L7.36744 16.303L5.28104 17.0913L4.29095 15.377L6.01903 13.9629L5.94127 13.6548C5.81911 13.1708 5.74847 12.6711 5.74847 12.1562C5.74847 11.6413 5.81911 11.1415 5.94127 10.6575L6.01903 10.3495L4.29095 8.93532L5.28095 7.22118L7.36733 8.01049L7.59538 7.78832C8.31241 7.08977 9.20233 6.56855 10.1931 6.28945L10.4996 6.20309ZM8.66504 12.1561C8.66504 10.3971 10.0904 8.97113 11.849 8.97113C13.6088 8.97113 15.034 10.3972 15.034 12.1561C15.034 13.9151 13.6088 15.3411 11.849 15.3411C10.0904 15.3411 8.66504 13.9152 8.66504 12.1561ZM11.849 7.97113C9.53772 7.97113 7.66504 9.84516 7.66504 12.1561C7.66504 14.4671 9.53772 16.3411 11.849 16.3411C14.1613 16.3411 16.034 14.4672 16.034 12.1561C16.034 9.84508 14.1613 7.97113 11.849 7.97113Z"
            fill={fillOrDefault}
          />
        </svg>
      )
    case 'settings-reverse':
      return (
        <svg width="20" height="20" viewBox="0 0 20 20" {...props}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M11.4068 2.50012H8.34102L8.01147 4.51765C7.24 4.77327 6.5381 5.18236 5.94407 5.71058L4.03309 4.98763L2.49976 7.64251L4.08271 8.9379C4.00281 9.32058 3.95682 9.71849 3.95682 10.1301C3.95682 10.5418 4.00281 10.9397 4.08271 11.3223L2.49976 12.6177L4.03301 15.2725L5.944 14.5504C6.53821 15.0787 7.24018 15.4872 8.01146 15.7432L8.34105 17.7601H11.4068L11.7363 15.7433C12.5083 15.4872 13.2099 15.0786 13.8045 14.5504L15.7156 15.2725L17.2489 12.6177L15.6654 11.3225C15.7458 10.9396 15.7918 10.5417 15.7918 10.1301C15.7918 9.71858 15.7458 9.32062 15.6654 8.9377L17.2489 7.64259L15.7155 4.98763L13.8044 5.71063C13.21 5.18245 12.5085 4.77326 11.7363 4.51759L11.4068 2.50012ZM7.22062 10.1301C7.22062 8.66422 8.40839 7.47591 9.87395 7.47591C11.3404 7.47591 12.5281 8.66429 12.5281 10.1301C12.5281 11.5959 11.3404 12.7842 9.87395 12.7842C8.40839 12.7842 7.22062 11.5959 7.22062 10.1301ZM9.87395 6.64258C7.94786 6.64258 6.38729 8.20427 6.38729 10.1301C6.38729 12.0559 7.94786 13.6176 9.87395 13.6176C11.8008 13.6176 13.3615 12.056 13.3615 10.1301C13.3615 8.2042 11.8008 6.64258 9.87395 6.64258Z"
            fill="white"
          />
        </svg>
      )
    case 'support':
      return (
        <svg height="24px" viewBox="0 -960 960 960" width="24px" {...props}>
          <path d="M470.77-160v-40h264.61q10 0 17.31-6.15 7.31-6.16 7.31-16.16v-275.54q0-112.38-82.65-187.73-82.66-75.34-197.35-75.34t-197.35 75.34Q200-610.23 200-497.85v227.08h-20q-24.54 0-42.27-16.58Q120-303.92 120-328.46v-75.39q0-17.92 11.65-31.8 11.66-13.89 28.35-22.58l.69-49.15q1.85-62.62 28.35-116 26.5-53.39 69.77-92.93 43.27-39.54 100.54-61.61Q416.62-800 480-800t120.54 22.08q57.15 22.07 100.54 61.5 43.38 39.42 69.77 92.8 26.38 53.39 28.46 116l.69 48.16q15.92 7.46 27.96 20.08Q840-426.77 840-409.08v86.62q0 17.69-12.04 30.31-12.04 12.61-27.96 20.07v49.77q0 26.08-18.88 44.19Q762.23-160 735.38-160H470.77Zm-95.39-258.46q-12.38 0-21.57-8.42-9.19-8.43-9.19-20.81 0-12.39 9.19-21.19 9.19-8.81 21.57-8.81 12.39 0 21.58 8.81 9.19 8.8 9.19 21.19 0 12.38-9.19 20.81-9.19 8.42-21.58 8.42Zm209.24 0q-12.39 0-21.58-8.42-9.19-8.43-9.19-20.81 0-12.39 9.19-21.19 9.19-8.81 21.58-8.81 12.38 0 21.57 8.81 9.19 8.8 9.19 21.19 0 12.38-9.19 20.81-9.19 8.42-21.57 8.42ZM268.69-482q-5.46-89.85 57.85-153.15 63.31-63.31 155.46-63.31 77.46 0 137.27 46.5 59.81 46.5 73.04 122.19-79.46-1-147.5-40.54t-104.43-110q-14.46 69.23-60.19 121.35-45.73 52.11-111.5 76.96Z" />
        </svg>
      )
    case 'info':
      return (
        <svg height="24px" viewBox="0 -960 960 960" width="24px" {...props}>
          <path d="M466-306h28v-214h-28v214Zm14-264q8.5 0 14.25-5.75T500-590q0-8.5-5.75-14.25T480-610q-8.5 0-14.25 5.75T460-590q0 8.5 5.75 14.25T480-570Zm.17 438q-72.17 0-135.73-27.39-63.56-27.39-110.57-74.35-47.02-46.96-74.44-110.43Q132-407.65 132-479.83q0-72.17 27.39-135.73 27.39-63.56 74.35-110.57 46.96-47.02 110.43-74.44Q407.65-828 479.83-828q72.17 0 135.73 27.39 63.56 27.39 110.57 74.35 47.02 46.96 74.44 110.43Q828-552.35 828-480.17q0 72.17-27.39 135.73-27.39 63.56-74.35 110.57-46.96 47.02-110.43 74.44Q552.35-132 480.17-132Zm-.17-28q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z" />
        </svg>
      )
    case 'dashboard':
      return (
        <svg width="21" height="20" viewBox="0 0 21 20" {...props}>
          <path d="M8.69814 2.58409C5.59822 2.58409 3.08421 5.09801 3.08421 8.19894C3.08421 11.2999 5.59822 13.8138 8.69814 13.8138C11.799 13.8138 14.313 11.2998 14.313 8.19894C14.313 5.09806 11.799 2.58409 8.69814 2.58409ZM2.16675 8.19894C2.16675 4.5914 5.09143 1.66663 8.69814 1.66663C12.3057 1.66663 15.2305 4.59136 15.2305 8.19894C15.2305 9.83764 14.627 11.3354 13.6302 12.4822L18.8325 17.6846L18.1838 18.3333L12.9814 13.131C11.8346 14.1278 10.3368 14.7313 8.69814 14.7313C5.09143 14.7313 2.16675 11.8065 2.16675 8.19894ZM6.99982 11.1776V8.19861H6.08236V11.1776H6.99982ZM9.15761 5.2206V11.1777H8.24015V5.2206H9.15761ZM11.3142 11.1775V6.74801H10.3967V11.1775H11.3142Z" />
        </svg>
      )
    case 'dashboard-reverse':
      return (
        <svg width="20" height="20" viewBox="0 0 20 20" {...props}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12.0572 12.6371C10.9549 13.6046 9.50996 14.1912 7.92809 14.1912C4.47059 14.1912 1.66675 11.3874 1.66675 7.92891C1.66675 4.47046 4.47059 1.66663 7.92809 1.66663C11.3865 1.66663 14.1904 4.47046 14.1904 7.92891C14.1904 9.51082 13.6038 10.9558 12.6362 12.0581L18.3324 17.7543L17.7534 18.3333L12.0572 12.6371ZM6.11313 7.92853V11.0001H5.29427V7.92853H6.11313ZM8.33847 11.0001V4.85795H7.51961V11.0001H8.33847ZM10.5618 6.43296V11.0001H9.74292V6.43296H10.5618Z"
            fill="white"
          />
        </svg>
      )
    case 'webstore':
      return (
        <svg width="21" height="20" viewBox="0 0 21 20" {...props}>
          <path d="M7.51737 4.16663L9.57758 11.2501H16.9725L18.7623 5.41663H9.03284L8.66826 4.16663H7.51737ZM6.41414 3.33329H9.29444L9.65902 4.58329H19.8989L17.5978 12.0834H8.94935L6.41414 3.33329ZM9.2501 14.1666C8.78986 14.1666 8.41677 14.5397 8.41677 14.9999C8.41677 15.4602 8.78986 15.8333 9.2501 15.8333C9.71034 15.8333 10.0834 15.4602 10.0834 14.9999C10.0834 14.5397 9.71034 14.1666 9.2501 14.1666ZM7.58344 14.9999C7.58344 14.0795 8.32962 13.3333 9.2501 13.3333C10.1706 13.3333 10.9168 14.0795 10.9168 14.9999C10.9168 15.9204 10.1706 16.6666 9.2501 16.6666C8.32962 16.6666 7.58344 15.9204 7.58344 14.9999ZM14.2501 14.1666C13.7899 14.1666 13.4168 14.5397 13.4168 14.9999C13.4168 15.4602 13.7899 15.8333 14.2501 15.8333C14.7103 15.8333 15.0834 15.4602 15.0834 14.9999C15.0834 14.5397 14.7103 14.1666 14.2501 14.1666ZM12.5834 14.9999C12.5834 14.0795 13.3296 13.3333 14.2501 13.3333C15.1706 13.3333 15.9168 14.0795 15.9168 14.9999C15.9168 15.9204 15.1706 16.6666 14.2501 16.6666C13.3296 16.6666 12.5834 15.9204 12.5834 14.9999Z" />
        </svg>
      )
    case 'academy':
      return (
        <svg width="21" height="20" viewBox="0 0 21 20" {...props}>
          <path d="M9.74825 2.5L3.83325 16.9267H5.31659L6.92492 12.9492H14.3033L15.9099 16.9267H17.4366L11.5616 2.5H9.74825ZM10.6141 3.81917L13.7666 11.6092H7.46075L10.6141 3.81917Z" />
        </svg>
      )
    case 'academy-reverse':
      return (
        <svg width="20" height="20" viewBox="0 0 20 20" {...props}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M3.66675 1.66663C2.56218 1.66663 1.66675 2.56206 1.66675 3.66663V16.3333C1.66675 17.4379 2.56218 18.3333 3.66675 18.3333H16.3334C17.438 18.3333 18.3334 17.4379 18.3334 16.3333V3.66663C18.3334 2.56206 17.438 1.66663 16.3334 1.66663H3.66675ZM4.16675 15.8333L8.95014 4.16663H10.4166L15.1676 15.8333H13.933L12.6337 12.6167H6.66694L5.3663 15.8333H4.16675ZM12.1998 11.5331L9.65038 5.23343L7.10032 11.5331H12.1998Z"
            fill="white"
          />
        </svg>
      )
    case 'infoScreen':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...rest}>
          <path
            d="M7.5 11C7.5 8.51932 9.51919 6.5 12.0003 6.5C14.4808 6.5 16.5001 8.51929 16.5001 11C16.5001 13.4812 14.4809 15.5 12.0003 15.5C9.51916 15.5 7.5 13.4812 7.5 11Z"
            stroke={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M11.7414 9.29634H12.3801V8.5H11.7414V9.29634ZM10.7998 12.495H11.802V10.2678H10.9099V9.76233H12.3251V12.495H13.2998V13H10.7998V12.495Z"
            fill={fillOrDefault}
          />
          <path d="M21.5 17.5H2.5V4.5H21.5V17.5Z" stroke={fillOrDefault} />
          <line x1="8" y1="19.5" x2="16" y2="19.5" stroke={fillOrDefault} />
        </svg>
      )
    case 'chain':
      return (
        <svg viewBox="0 0 24 24" width="24" height="24" {...props}>
          <path d="M10.94 21.77H7.29v-3.52H5.65v3.52H2v-6.53l4.42-2.68 4.51 2.68v6.53zm-2.65-1h1.64v-4.96l-3.5-2.08L3 15.81v4.96h1.64v-3.52h3.64v3.52zM21.23 21.77h-3.64v-3.52h-1.64v3.52h-3.64v-6.53l4.42-2.68 4.51 2.68v6.53zm-2.65-1h1.64v-4.96l-3.51-2.08-3.43 2.08v4.96h1.64v-3.52h3.64v3.52zM15.76 11.59h-3.64V8.07h-1.64v3.52H6.83V5.07l4.42-2.68 4.51 2.68v6.52zm-2.64-1h1.64V5.63l-3.5-2.08-3.43 2.08v4.96h1.64V7.07h3.64v3.52z" />
        </svg>
      )
    case 'logout':
      return (
        <svg viewBox="0 0 24 24" width="24" height="24" {...props}>
          <path d="M17.12 21.16H3.31V2.84h13.81V6h-1V3.84H4.31v16.32h11.81V18h1z" />
          <path d="M16.33 7.44l-.7.71 3.35 3.35h-8.62v1h8.62l-3.35 3.35.7.71L20.89 12z" />
        </svg>
      )
    case 'defaultPhoto':
      return (
        <svg viewBox="0 0 48 48" width="48" height="48" {...props}>
          <path d="M23.99 22.02c-4.27 0-7.74-3.47-7.74-7.74s3.47-7.74 7.74-7.74 7.74 3.47 7.74 7.74-3.48 7.74-7.74 7.74zm0-14.48c-3.72 0-6.74 3.02-6.74 6.74s3.02 6.74 6.74 6.74 6.74-3.02 6.74-6.74-3.03-6.74-6.74-6.74zM41.53 41.74H6.44l.01-.51c.26-9.58 7.96-17.08 17.53-17.08s17.28 7.5 17.53 17.08l.02.51zm-34.06-1H40.5c-.5-8.79-7.66-15.59-16.51-15.59S7.97 31.96 7.47 40.74z" />
        </svg>
      )
    case 'arrow-up-down':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...rest}>
          <g>
            <rect opacity="0.01" width="24" height="24" fill={fillOrDefault} />
            <g>
              <path d="M16.331 14.7072L11.842 19.1962L7.354 14.7072" stroke={fillOrDefault} />
              <path d="M16.331 9.19591L11.842 4.70691L7.354 9.19591" stroke={fillOrDefault} />
            </g>
          </g>
        </svg>
      )
    case 'arrowhead-down-small':
      return (
        <svg viewBox="0 0 24 24" width="24" height="24" {...rest}>
          <path d="M12 17.03l-9.01-9 .71-.71 8.3 8.3 8.3-8.3.71.71z" stroke={fillOrDefault} />
        </svg>
      )
    case 'arrowhead-down-xsmall':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...rest}>
          <path d="M16.331 10L11.842 14.489L7.354 10" stroke={fillOrDefault} />
        </svg>
      )
    case 'arrowhead-up-small':
      return (
        <svg viewBox="0 0 24 24" width="24" height="24" {...rest}>
          <path d="M20.3 16.68L12 8.38l-8.3 8.3-.71-.71 9.01-9 9.01 9z" stroke={fillOrDefault} />
        </svg>
      )
    case 'arrowhead-up-xsmall':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...rest}>
          <path d="M16.3305 14.489L11.8415 10L7.35352 14.489" stroke={fillOrDefault} />
        </svg>
      )
    case 'arrowhead-right-small':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" {...rest}>
          <path
            d="M16.2929 12.2471L7.6416 3.34857L8.3586 2.65149L17.7073 12.2673L8.34849 21.3587L7.65171 20.6414L16.2929 12.2471Z"
            fill={fillOrDefault}
          />
        </svg>
      )
    case 'arrowhead-right-xsmall':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...rest}>
          <path d="M10 7L14.559 11.559L10 16.117" stroke={fillOrDefault} />
        </svg>
      )
    case 'arrowhead-left-small':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" {...rest}>
          <path
            d="M16.6518 2.64136L17.3485 3.35864L8.70733 11.753L17.3586 20.6515L16.6417 21.3485L7.29297 11.7327L16.6518 2.64136Z"
            fill={fillOrDefault}
          />
        </svg>
      )
    case 'arrowhead-left-xsmall':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...rest}>
          <path d="M14.0869 16.733L9.59791 12.244L14.0869 7.75598" stroke={fillOrDefault} />
        </svg>
      )
    case 'dishwasher':
      return (
        <svg width="47" height="47" viewBox="0 0 47 47" fill="none" {...rest}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M7.82992 5.78988C6.72589 5.78988 5.82992 6.68495 5.82992 7.78988C5.82992 8.89369 6.72611 9.78988 7.82992 9.78988C8.93373 9.78988 9.82992 8.89369 9.82992 7.78988C9.82992 6.68495 8.93394 5.78988 7.82992 5.78988ZM4.49658 7.78988C4.49658 5.94814 5.98994 4.45654 7.82992 4.45654C9.66989 4.45654 11.1632 5.94814 11.1632 7.78988C11.1632 9.63007 9.67011 11.1232 7.82992 11.1232C5.98973 11.1232 4.49658 9.63007 4.49658 7.78988Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M39.8109 5.78988C38.7068 5.78988 37.8109 6.68495 37.8109 7.78988C37.8109 8.89369 38.7071 9.78988 39.8109 9.78988C40.9147 9.78988 41.8109 8.89369 41.8109 7.78988C41.8109 6.68495 40.9149 5.78988 39.8109 5.78988ZM36.4775 7.78988C36.4775 5.94814 37.9709 4.45654 39.8109 4.45654C41.6508 4.45654 43.1442 5.94814 43.1442 7.78988C43.1442 9.63007 41.6511 11.1232 39.8109 11.1232C37.9707 11.1232 36.4775 9.63007 36.4775 7.78988Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M31.1444 5.78988C30.0403 5.78988 29.1444 6.68495 29.1444 7.78988C29.1444 8.89369 30.0406 9.78988 31.1444 9.78988C32.2482 9.78988 33.1444 8.89369 33.1444 7.78988C33.1444 6.68495 32.2484 5.78988 31.1444 5.78988ZM27.811 7.78988C27.811 5.94814 29.3044 4.45654 31.1444 4.45654C32.9843 4.45654 34.4777 5.94814 34.4777 7.78988C34.4777 9.63007 32.9846 11.1232 31.1444 11.1232C29.3042 11.1232 27.811 9.63007 27.811 7.78988Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M22.4774 5.78988C21.3734 5.78988 20.4774 6.68495 20.4774 7.78988C20.4774 8.89369 21.3736 9.78988 22.4774 9.78988C23.5812 9.78988 24.4774 8.89369 24.4774 7.78988C24.4774 6.68495 23.5814 5.78988 22.4774 5.78988ZM19.144 7.78988C19.144 5.94814 20.6374 4.45654 22.4774 4.45654C24.3174 4.45654 25.8107 5.94814 25.8107 7.78988C25.8107 9.63007 24.3176 11.1232 22.4774 11.1232C20.6372 11.1232 19.144 9.63007 19.144 7.78988Z"
            fill={fillOrDefault}
          />
          <mask
            id="iconDishwasherMask0"
            mask-type="alpha"
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="48"
            height="47"
          >
            <path fillRule="evenodd" clipRule="evenodd" d="M0.333496 47H47.0002V0.333313H0.333496V47Z" fill="white" />
          </mask>
          <g mask="url(#iconDishwasherMask0)">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M0.333496 0.333313H47.0002V47H0.333496V0.333313ZM1.66683 1.66665V45.6666H45.6668V1.66665H1.66683Z"
              fill={fillOrDefault}
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M31.667 25.6661C29.0898 25.6661 27.0003 27.7556 27.0003 30.3328C27.0003 32.9111 29.0897 34.9994 31.667 34.9994C34.2442 34.9994 36.3337 32.9111 36.3337 30.3328C36.3337 27.7556 34.2441 25.6661 31.667 25.6661ZM25.667 30.3328C25.667 27.0192 28.3535 24.3328 31.667 24.3328C34.9805 24.3328 37.667 27.0192 37.667 30.3328C37.667 33.6477 34.9804 36.3328 31.667 36.3328C28.3536 36.3328 25.667 33.6477 25.667 30.3328Z"
              fill={fillOrDefault}
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M31.6668 20.3328C26.1444 20.3328 21.6668 24.8103 21.6668 30.3328C21.6668 35.8565 26.1443 40.3328 31.6668 40.3328C37.1894 40.3328 41.6668 35.8565 41.6668 30.3328C41.6668 24.8103 37.1893 20.3328 31.6668 20.3328ZM20.3335 30.3328C20.3335 24.0739 25.408 18.9995 31.6668 18.9995C37.9257 18.9995 43.0002 24.0739 43.0002 30.3328C43.0002 36.593 37.9256 41.6661 31.6668 41.6661C25.408 41.6661 20.3335 36.593 20.3335 30.3328Z"
              fill={fillOrDefault}
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M5.66699 20.3328H17.667V24.9994C17.667 28.3144 14.9804 30.9994 11.667 30.9994C8.35358 30.9994 5.66699 28.3144 5.66699 24.9994V20.3328ZM7.00033 21.6661V24.9994C7.00033 27.5778 9.08974 29.6661 11.667 29.6661C14.2442 29.6661 16.3337 27.5778 16.3337 24.9994V21.6661H7.00033Z"
              fill={fillOrDefault}
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M6.3335 38.9995H17.0002V40.3328H6.3335V38.9995Z"
              fill={fillOrDefault}
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M9.66699 39.6668V32.4201H11.0003V39.6668H9.66699Z"
              fill={fillOrDefault}
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M12.3335 39.6668V32.4201H13.6668V39.6668H12.3335Z"
              fill={fillOrDefault}
            />
          </g>
        </svg>
      )
    case 'fridge':
      return (
        <svg width="34" height="47" viewBox="0 0 34 47" fill="none" {...rest}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M-0.000244141 3.66744C-0.000244141 1.8257 1.49312 0.334106 3.33309 0.334106H29.9998C31.8397 0.334106 33.3331 1.8257 33.3331 3.66744V15.0008H-0.000244141V3.66744ZM3.33309 1.66744C2.22906 1.66744 1.33309 2.56251 1.33309 3.66744V13.6674H31.9998V3.66744C31.9998 2.56251 31.1038 1.66744 29.9998 1.66744H3.33309Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M-0.000244141 17.6674H33.3331V43.6674C33.3331 45.5076 31.8399 47.0008 29.9998 47.0008H3.33309C1.4929 47.0008 -0.000244141 45.5076 -0.000244141 43.6674V17.6674ZM1.33309 19.0008V43.6674C1.33309 44.7712 2.22928 45.6674 3.33309 45.6674H29.9998C31.1036 45.6674 31.9998 44.7712 31.9998 43.6674V19.0008H1.33309Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M5.33301 10.3336V5.00024H6.66634V10.3336H5.33301Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M6.66634 22.3336V41.0002H5.33301V22.3336H6.66634Z"
            fill={fillOrDefault}
          />
        </svg>
      )
    case 'fridge-reverse':
      return (
        <svg width="16" height="22" viewBox="0 0 16 22" fill="none" {...rest}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M2.43325 0.5C1.27346 0.5 0.333252 1.4402 0.333252 2.6V7.18183H15.606V2.6C15.606 1.4402 14.6658 0.5 13.506 0.5H2.43325ZM4.00826 5.22501H2.95826V2.60001H4.00826V5.22501ZM0.333252 8.13634V19.4C0.333252 20.5598 1.27346 21.5 2.43326 21.5H13.506C14.6658 21.5 15.606 20.5598 15.606 19.4V8.13634H0.333252ZM4.00826 18.35H2.95826V9.95004H4.00826V18.35Z"
            fill={fillOrDefault}
          />
        </svg>
      )
    case 'scale':
      return (
        <svg width="47" height="47" viewBox="0 0 47 47" fill="none" {...rest}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M45.6666 1.66665H1.66659V45.6666H45.6666V1.66665ZM0.333252 0.333313V47H46.9999V0.333313H0.333252Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8.11108 12.8105C8.1116 12.8097 8.11326 12.8068 8.11578 12.8023C8.14634 12.7476 8.32702 12.4242 8.72196 11.9536C8.80289 11.8572 8.8928 11.7546 8.99228 11.6469C10.6023 9.90322 14.7136 6.81482 23.6666 6.81482C36.1534 6.81482 39.2222 12.8105 39.2222 12.8105L30.9682 19.7778H16.365L8.11108 12.8105ZM10.1111 12.5913C10.5206 12.1507 11.1382 11.5891 12.0248 11.0176C14.0825 9.69147 17.6793 8.25515 23.6666 8.25515C29.6546 8.25515 33.2515 9.68884 35.3089 11.0135C36.1976 11.5857 36.8158 12.1481 37.2248 12.5891L30.4148 18.3375H16.9184L10.1111 12.5913Z"
            fill={fillOrDefault}
          />
          <path d="M23.6666 13.2963L25.9118 19.1296H21.4213L23.6666 13.2963Z" fill={fillOrDefault} />
        </svg>
      )
    case 'cooler':
      return (
        <svg width="45" height="48" viewBox="0 0 45 48" fill="none" {...rest}>
          <path
            d="M8.17889 11.6257C3.9896 15.3321 1.33252 20.7339 1.33252 26.7683C1.33252 37.9416 10.3909 46.9999 21.5641 46.9999C32.7373 46.9999 41.7957 37.9416 41.7957 26.7683C41.7957 20.7339 39.14 15.3321 34.9507 11.6257"
            stroke={fillOrDefault}
          />
          <path d="M21.5645 0.333801V9.00506" stroke={fillOrDefault} />
          <path d="M21.5645 16.4769V7.80426" stroke={fillOrDefault} />
          <path d="M14.5747 4.36945L22.0847 8.7044" stroke={fillOrDefault} />
          <path d="M28.5544 12.441L21.0444 8.10474" stroke={fillOrDefault} />
          <path d="M14.5747 12.441L22.0847 8.10474" stroke={fillOrDefault} />
          <path d="M28.5544 4.36945L21.0444 8.7044" stroke={fillOrDefault} />
          <path d="M34.8003 21.3267V11.6263H44.502" stroke={fillOrDefault} />
        </svg>
      )
    case 'freezer':
      return (
        <svg width="44" height="44" viewBox="0 0 44 44" fill="none" {...rest}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M0.666748 0.666687H43.3334V43.3334H0.666748V0.666687ZM1.8858 1.88573V42.1143H42.1144V1.88573H1.8858Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M21.3909 23.2228V6.15247H22.6099V23.2228H21.3909Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M21.6626 9.30243L24.4883 7.41779L25.1648 8.43196L22.339 10.3166L21.6626 9.30243Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M21.6697 13.9057L26.5642 10.5972L27.2468 11.6072L22.3524 14.9157L21.6697 13.9057Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M21.6622 10.3167L18.8352 8.4321L19.5114 7.41779L22.3384 9.30243L21.6622 10.3167Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M21.6484 14.9157L16.7539 11.6072L17.4366 10.5972L22.3311 13.9057L21.6484 14.9157Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M22.6099 20.735V37.8054H21.3909V20.735H22.6099Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M22.3384 34.6553L19.5114 36.5399L18.8352 35.5256L21.6622 33.641L22.3384 34.6553Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M22.3311 30.0518L17.4366 33.3603L16.7539 32.3504L21.6484 29.0419L22.3311 30.0518Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M22.339 33.641L25.1648 35.5256L24.4883 36.5398L21.6626 34.6552L22.339 33.641Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M22.3524 29.0419L27.2468 32.3504L26.5642 33.3603L21.6697 30.0518L22.3524 29.0419Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M22.7724 23.1291L7.98901 14.5933L8.59857 13.5376L23.382 22.0734L22.7724 23.1291Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.8525 15.9334L10.6343 12.5433L11.8508 12.465L12.069 15.8551L10.8525 15.9334Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14.8434 18.2276L14.4253 12.336L15.6413 12.2497L16.0594 18.1414L14.8434 18.2276Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M11.731 16.441L8.68578 17.9465L8.14551 16.8537L11.1907 15.3482L11.731 16.441Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M15.7075 18.7519L10.3948 21.3362L9.86157 20.24L15.1742 17.6556L15.7075 18.7519Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M21.228 20.8286L36.0114 29.3644L35.4018 30.4201L20.6184 21.8843L21.228 20.8286Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M33.1477 28.024L33.3659 31.4142L32.1494 31.4925L31.9312 28.1023L33.1477 28.024Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M29.1572 25.7289L29.5753 31.6218L28.3593 31.708L27.9412 25.8152L29.1572 25.7289Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M32.269 27.5168L35.3142 26.0112L35.8545 27.104L32.8093 28.6095L32.269 27.5168Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M28.2927 25.2051L33.6053 22.6207L34.1386 23.7169L28.826 26.3013L28.2927 25.2051Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M23.382 21.8843L8.59857 30.4201L7.98901 29.3644L22.7724 20.8286L23.382 21.8843Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M11.1907 28.6095L8.14551 27.104L8.68578 26.0112L11.731 27.5168L11.1907 28.6095Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M15.1742 26.3013L9.86157 23.7169L10.3948 22.6207L15.7075 25.2051L15.1742 26.3013Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12.069 28.1023L11.8508 31.4925L10.6343 31.4142L10.8525 28.024L12.069 28.1023Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M16.0594 25.8152L15.6413 31.708L14.4253 31.6218L14.8434 25.7289L16.0594 25.8152Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M20.6184 22.0734L35.4018 13.5376L36.0114 14.5933L21.228 23.1291L20.6184 22.0734Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M32.8093 15.3482L35.8545 16.8537L35.3142 17.9465L32.269 16.441L32.8093 15.3482Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M28.826 17.6556L34.1386 20.24L33.6053 21.3362L28.2927 18.7519L28.826 17.6556Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M31.9312 15.8551L32.1494 12.465L33.3659 12.5433L33.1477 15.9334L31.9312 15.8551Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M27.9412 18.1414L28.3593 12.2497L29.5753 12.336L29.1572 18.2276L27.9412 18.1414Z"
            fill={fillOrDefault}
          />
        </svg>
      )
    case 'oven':
      return (
        <svg width="44" height="44" viewBox="0 0 44 44" fill="none" {...rest}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M9.95847 7.48363C9.95847 8.82946 8.8662 9.92172 7.52037 9.92172C6.17454 9.92172 5.08228 8.82946 5.08228 7.48363C5.08228 6.1378 6.17454 5.04553 7.52037 5.04553C8.8662 5.04553 9.95847 6.1378 9.95847 7.48363Z"
            stroke={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M39.1985 7.48363C39.1985 8.82946 38.1062 9.92172 36.7604 9.92172C35.4145 9.92172 34.3223 8.82946 34.3223 7.48363C34.3223 6.1378 35.4145 5.04553 36.7604 5.04553C38.1062 5.04553 39.1985 6.1378 39.1985 7.48363Z"
            stroke={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M31.2746 7.48363C31.2746 8.82946 30.1824 9.92172 28.8365 9.92172C27.4907 9.92172 26.3984 8.82946 26.3984 7.48363C26.3984 6.1378 27.4907 5.04553 28.8365 5.04553C30.1824 5.04553 31.2746 6.1378 31.2746 7.48363Z"
            stroke={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M23.3506 7.48363C23.3506 8.82946 22.2583 9.92172 20.9125 9.92172C19.5666 9.92172 18.4744 8.82946 18.4744 7.48363C18.4744 6.1378 19.5666 5.04553 20.9125 5.04553C22.2583 5.04553 23.3506 6.1378 23.3506 7.48363Z"
            stroke={fillOrDefault}
          />
          <mask id="iconOvenMask0" mask-type="alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="44" height="44">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M0.666748 43.3334H43.3334V0.666687H0.666748V43.3334Z"
              fill="white"
            />
          </mask>
          <g mask="url(#iconOvenMask0)">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M1.27637 42.7239H42.724V1.27625H1.27637V42.7239Z"
              stroke={fillOrDefault}
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M1.27637 36.5554H42.724V18.3441H1.27637V36.5554Z"
              stroke={fillOrDefault}
            />
            <path d="M7.37134 23.2195H36.6285" stroke={fillOrDefault} />
          </g>
        </svg>
      )
    case 'wash':
      return (
        <svg width="34" height="29" viewBox="0 0 34 29" fill="none" {...rest}>
          <path
            d="M33.4929 15.0311C33.4007 15.0058 33.3034 14.9958 33.2064 15.0016C33.1095 15.0073 33.0149 15.0288 32.9281 15.0647C32.8413 15.1006 32.7639 15.1502 32.7004 15.2108C32.6368 15.2713 32.5885 15.3416 32.5579 15.4176L30.8358 19.6783L30.7176 19.8C29.9563 20.5973 29.439 21.0873 28.4005 21.0873C27.3621 21.0873 26.8521 20.5973 26.0945 19.8C25.337 19.0026 24.4242 18.044 22.6134 18.044C20.8026 18.044 19.8824 19.0179 19.1396 19.8C18.3968 20.5821 17.861 21.0873 16.8262 21.0873C15.7915 21.0873 15.2741 20.5973 14.5165 19.8C13.759 19.0026 12.8462 18.044 11.0391 18.044C9.23198 18.044 8.30811 19.0179 7.56162 19.8C6.81513 20.5821 6.28298 21.0873 5.25563 21.0873C4.91609 21.0856 4.58157 21.0196 4.27786 20.8946C3.97416 20.7695 3.70939 20.5887 3.50397 20.3661C3.47747 20.3422 3.44908 20.3198 3.41897 20.2991L1.44188 15.4298C1.41131 15.3538 1.36287 15.2836 1.29934 15.2231C1.23581 15.1626 1.15842 15.1129 1.0716 15.0771C0.984782 15.0412 0.890229 15.0197 0.793341 15.0139C0.696453 15.0081 0.599127 15.0181 0.50692 15.0433C0.414713 15.0684 0.329431 15.1083 0.255943 15.1607C0.182455 15.213 0.122201 15.2767 0.0786195 15.3482C0.0350384 15.4197 0.00898432 15.4976 0.00194484 15.5774C-0.00509464 15.6571 0.00701829 15.7373 0.0375922 15.8132L5.21129 28.5952C5.26274 28.7144 5.358 28.8174 5.48384 28.89C5.60968 28.9625 5.75983 29.001 5.91343 29H28.0864C28.2418 29.0001 28.3933 28.9599 28.5193 28.885C28.6454 28.8102 28.7396 28.7045 28.7885 28.583L33.9622 15.8011C33.9929 15.7251 34.0051 15.645 33.9981 15.5652C33.9911 15.4853 33.965 15.4074 33.9214 15.3359C33.8779 15.2644 33.8176 15.2007 33.744 15.1484C33.6705 15.0961 33.5852 15.0562 33.4929 15.0311ZM27.5542 27.7826H6.44558L4.17655 22.1707C4.52574 22.2608 4.88972 22.306 5.25563 22.3047C7.05903 22.3047 7.98291 21.3308 8.7257 20.5487C9.4685 19.7665 10.0043 19.2613 11.0391 19.2613C12.0738 19.2613 12.5912 19.7513 13.3525 20.5487C14.1137 21.346 15.0191 22.3047 16.8262 22.3047C18.6333 22.3047 19.5572 21.3308 20.3037 20.5487C21.0502 19.7665 21.5786 19.2613 22.6134 19.2613C23.6481 19.2613 24.1692 19.7513 24.9304 20.5517C25.6917 21.3521 26.6082 22.3047 28.4005 22.3047C28.9101 22.3099 29.4148 22.2226 29.8787 22.049L27.5542 27.7826Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M21.625 5.15625C21.625 8.00397 19.3165 10.3125 16.4688 10.3125C13.621 10.3125 11.3125 8.00397 11.3125 5.15625C11.3125 2.30853 13.621 0 16.4688 0C19.3165 0 21.625 2.30853 21.625 5.15625ZM16.4688 9.28125C18.7469 9.28125 20.5938 7.43442 20.5938 5.15625C20.5938 2.87808 18.7469 1.03125 16.4688 1.03125C14.1906 1.03125 12.3438 2.87808 12.3438 5.15625C12.3438 7.43442 14.1906 9.28125 16.4688 9.28125Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M28.8438 10.3125C28.8438 12.0211 27.4586 13.4062 25.75 13.4062C24.0414 13.4062 22.6562 12.0211 22.6562 10.3125C22.6562 8.60387 24.0414 7.21875 25.75 7.21875C27.4586 7.21875 28.8438 8.60387 28.8438 10.3125ZM25.75 12.375C26.8891 12.375 27.8125 11.4516 27.8125 10.3125C27.8125 9.17341 26.8891 8.25 25.75 8.25C24.6109 8.25 23.6875 9.17341 23.6875 10.3125C23.6875 11.4516 24.6109 12.375 25.75 12.375Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M11.3125 11.8594C11.3125 13.2832 10.1582 14.4375 8.73438 14.4375C7.31052 14.4375 6.15625 13.2832 6.15625 11.8594C6.15625 10.4355 7.31052 9.28125 8.73438 9.28125C10.1582 9.28125 11.3125 10.4355 11.3125 11.8594ZM8.73438 13.4062C9.58869 13.4062 10.2812 12.7137 10.2812 11.8594C10.2812 11.0051 9.58869 10.3125 8.73438 10.3125C7.88006 10.3125 7.1875 11.0051 7.1875 11.8594C7.1875 12.7137 7.88006 13.4062 8.73438 13.4062Z"
            fill={fillOrDefault}
          />
        </svg>
      )
    case 'rinse':
      return (
        <svg width="34" height="30" viewBox="0 0 34 30" fill="none" {...rest}>
          <path
            d="M33.4929 16.0311C33.4007 16.0058 33.3034 15.9958 33.2064 16.0016C33.1095 16.0073 33.0149 16.0288 32.9281 16.0647C32.8413 16.1006 32.7639 16.1502 32.7004 16.2108C32.6368 16.2713 32.5885 16.3416 32.5579 16.4176L30.8358 20.6783L30.7176 20.8C29.9563 21.5973 29.439 22.0873 28.4005 22.0873C27.3621 22.0873 26.8521 21.5973 26.0945 20.8C25.337 20.0026 24.4242 19.044 22.6134 19.044C20.8026 19.044 19.8824 20.0179 19.1396 20.8C18.3968 21.5821 17.861 22.0873 16.8262 22.0873C15.7915 22.0873 15.2741 21.5973 14.5165 20.8C13.759 20.0026 12.8462 19.044 11.0391 19.044C9.23198 19.044 8.30811 20.0179 7.56162 20.8C6.81513 21.5821 6.28298 22.0873 5.25563 22.0873C4.91609 22.0856 4.58157 22.0196 4.27786 21.8946C3.97416 21.7695 3.70939 21.5887 3.50397 21.3661C3.47747 21.3422 3.44908 21.3198 3.41897 21.2991L1.44188 16.4298C1.41131 16.3538 1.36287 16.2836 1.29934 16.2231C1.23581 16.1626 1.15842 16.1129 1.0716 16.0771C0.984782 16.0412 0.890229 16.0197 0.793341 16.0139C0.696453 16.0081 0.599127 16.0181 0.50692 16.0433C0.414713 16.0684 0.329431 16.1083 0.255943 16.1607C0.182455 16.213 0.122201 16.2767 0.0786195 16.3482C0.0350384 16.4197 0.00898432 16.4976 0.00194484 16.5774C-0.00509464 16.6571 0.00701829 16.7373 0.0375922 16.8132L5.21129 29.5952C5.26274 29.7144 5.358 29.8174 5.48384 29.89C5.60968 29.9625 5.75983 30.001 5.91343 30H28.0864C28.2418 30.0001 28.3933 29.9599 28.5193 29.885C28.6454 29.8102 28.7396 29.7045 28.7885 29.583L33.9622 16.8011C33.9929 16.7251 34.0051 16.645 33.9981 16.5652C33.9911 16.4853 33.965 16.4074 33.9214 16.3359C33.8779 16.2644 33.8176 16.2007 33.744 16.1484C33.6705 16.0961 33.5852 16.0562 33.4929 16.0311ZM27.5542 28.7826H6.44558L4.17655 23.1707C4.52574 23.2608 4.88972 23.306 5.25563 23.3047C7.05903 23.3047 7.98291 22.3308 8.7257 21.5487C9.4685 20.7665 10.0043 20.2613 11.0391 20.2613C12.0738 20.2613 12.5912 20.7513 13.3525 21.5487C14.1137 22.346 15.0191 23.3047 16.8262 23.3047C18.6333 23.3047 19.5572 22.3308 20.3037 21.5487C21.0502 20.7665 21.5786 20.2613 22.6134 20.2613C23.6481 20.2613 24.1692 20.7513 24.9304 21.5517C25.6917 22.3521 26.6082 23.3047 28.4005 23.3047C28.9101 23.3099 29.4148 23.2226 29.8787 23.049L27.5542 28.7826Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M18.3899 0.119233C18.122 0.184981 17.9591 0.451481 18.0261 0.714475L18.2609 1.63665C18.3279 1.89964 18.5993 2.05954 18.8672 1.9938C19.1351 1.92805 19.298 1.66155 19.231 1.39855L18.9962 0.476378C18.9292 0.213384 18.6578 0.0534841 18.3899 0.119233ZM19.0944 2.88576C18.8265 2.95151 18.6636 3.218 18.7306 3.481L19.2003 5.32535C19.2672 5.58834 19.5387 5.74824 19.8066 5.68249C20.0745 5.61675 20.2374 5.35025 20.1704 5.08725L19.7007 3.2429C19.6337 2.97991 19.3623 2.82001 19.0944 2.88576ZM20.0337 6.57446C19.7658 6.6402 19.603 6.9067 19.6699 7.1697L20.1396 9.01405C20.2066 9.27704 20.4781 9.43694 20.746 9.37119C21.0139 9.30544 21.1767 9.03895 21.1098 8.77595L20.6401 6.9316C20.5731 6.66861 20.3016 6.50871 20.0337 6.57446ZM20.9731 10.2632C20.7052 10.3289 20.5423 10.5954 20.6093 10.8584L21.079 12.7027C21.146 12.9657 21.4174 13.1256 21.6853 13.0599C21.9532 12.9941 22.1161 12.7276 22.0491 12.4647L21.5795 10.6203C21.5125 10.3573 21.241 10.1974 20.9731 10.2632ZM21.9125 13.9519C21.6446 14.0176 21.4817 14.2841 21.5487 14.5471L21.7835 15.4693C21.8505 15.7323 22.122 15.8922 22.3899 15.8264C22.6578 15.7607 22.8206 15.4942 22.7537 15.2312L22.5188 14.309C22.4518 14.046 22.1804 13.8861 21.9125 13.9519Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M15.3601 0.119233C15.628 0.184981 15.7909 0.451481 15.7239 0.714475L15.4891 1.63665C15.4221 1.89964 15.1507 2.05954 14.8828 1.9938C14.6149 1.92805 14.452 1.66155 14.519 1.39855L14.7538 0.476378C14.8208 0.213384 15.0922 0.0534841 15.3601 0.119233ZM14.6556 2.88576C14.9235 2.95151 15.0864 3.218 15.0194 3.481L14.5497 5.32535C14.4828 5.58834 14.2113 5.74824 13.9434 5.68249C13.6755 5.61675 13.5126 5.35025 13.5796 5.08725L14.0493 3.2429C14.1163 2.97991 14.3877 2.82001 14.6556 2.88576ZM13.7163 6.57446C13.9842 6.6402 14.147 6.9067 14.0801 7.1697L13.6104 9.01405C13.5434 9.27704 13.2719 9.43694 13.004 9.37119C12.7361 9.30544 12.5733 9.03895 12.6402 8.77595L13.1099 6.9316C13.1769 6.66861 13.4484 6.50871 13.7163 6.57446ZM12.7769 10.2632C13.0448 10.3289 13.2077 10.5954 13.1407 10.8584L12.671 12.7027C12.604 12.9657 12.3326 13.1256 12.0647 13.0599C11.7968 12.9941 11.6339 12.7276 11.7009 12.4647L12.1705 10.6203C12.2375 10.3573 12.509 10.1974 12.7769 10.2632ZM11.8375 13.9519C12.1054 14.0176 12.2683 14.2841 12.2013 14.5471L11.9665 15.4693C11.8995 15.7323 11.628 15.8922 11.3601 15.8264C11.0922 15.7607 10.9294 15.4942 10.9963 15.2312L11.2312 14.309C11.2982 14.046 11.5696 13.8861 11.8375 13.9519Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M11.3523 0.146439C11.6159 0.227298 11.7628 0.502604 11.6805 0.761353L11.3866 1.68449C11.3042 1.94324 11.0238 2.08745 10.7602 2.00659C10.4966 1.92573 10.3497 1.65042 10.4321 1.39167L10.726 0.468536C10.8083 0.209788 11.0888 0.0655796 11.3523 0.146439ZM10.4708 2.91585C10.7343 2.9967 10.8812 3.27201 10.7989 3.53076L10.2112 5.37703C10.1288 5.63578 9.84836 5.77999 9.58479 5.69913C9.32121 5.61827 9.17432 5.34296 9.25668 5.08422L9.8444 3.23794C9.92677 2.97919 10.2072 2.83499 10.4708 2.91585ZM9.29535 6.60839C9.55892 6.68925 9.70581 6.96455 9.62345 7.2233L9.03573 9.06957C8.95337 9.32832 8.67293 9.47253 8.40935 9.39167C8.14578 9.31081 7.99889 9.03551 8.08125 8.77676L8.66897 6.93049C8.75133 6.67174 9.03177 6.52753 9.29535 6.60839ZM8.11991 10.3009C8.38349 10.3818 8.53038 10.6571 8.44802 10.9158L7.8603 12.7621C7.77793 13.0209 7.4975 13.1651 7.23392 13.0842C6.97035 13.0034 6.82345 12.7281 6.90582 12.4693L7.49354 10.623C7.5759 10.3643 7.85634 10.2201 8.11991 10.3009ZM6.94448 13.9935C7.20805 14.0743 7.35495 14.3496 7.27258 14.6084L6.97873 15.5315C6.89636 15.7903 6.61592 15.9345 6.35235 15.8536C6.08878 15.7728 5.94188 15.4975 6.02425 15.2387L6.3181 14.3156C6.40047 14.0568 6.68091 13.9126 6.94448 13.9935Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M22.3977 0.146439C22.1341 0.227298 21.9872 0.502604 22.0695 0.761353L22.3634 1.68449C22.4458 1.94324 22.7262 2.08745 22.9898 2.00659C23.2534 1.92573 23.4003 1.65042 23.3179 1.39167L23.024 0.468536C22.9417 0.209788 22.6612 0.0655796 22.3977 0.146439ZM23.2792 2.91585C23.0157 2.9967 22.8688 3.27201 22.9511 3.53076L23.5388 5.37703C23.6212 5.63578 23.9016 5.77999 24.1652 5.69913C24.4288 5.61827 24.5757 5.34296 24.4933 5.08422L23.9056 3.23794C23.8232 2.97919 23.5428 2.83499 23.2792 2.91585ZM24.4547 6.60839C24.1911 6.68925 24.0442 6.96455 24.1266 7.2233L24.7143 9.06957C24.7966 9.32832 25.0771 9.47253 25.3406 9.39167C25.6042 9.31081 25.7511 9.03551 25.6687 8.77676L25.081 6.93049C24.9987 6.67174 24.7182 6.52753 24.4547 6.60839ZM25.6301 10.3009C25.3665 10.3818 25.2196 10.6571 25.302 10.9158L25.8897 12.7621C25.9721 13.0209 26.2525 13.1651 26.5161 13.0842C26.7796 13.0034 26.9265 12.7281 26.8442 12.4693L26.2565 10.623C26.1741 10.3643 25.8937 10.2201 25.6301 10.3009ZM26.8055 13.9935C26.5419 14.0743 26.395 14.3496 26.4774 14.6084L26.7713 15.5315C26.8536 15.7903 27.1341 15.9345 27.3977 15.8536C27.6612 15.7728 27.8081 15.4975 27.7258 15.2387L27.4319 14.3156C27.3495 14.0568 27.0691 13.9126 26.8055 13.9935Z"
            fill={fillOrDefault}
          />
        </svg>
      )

    case 'check':
      return (
        <svg width="19" height="13" viewBox="0 0 19 13" fill="none" {...rest}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M6.55404 11.3999L17.3075 0.646484L18.0146 1.35359L6.55404 12.8141L0.646484 6.90659L1.35359 6.19948L6.55404 11.3999Z"
            fill={fillOrDefault}
          />
        </svg>
      )
    case 'exclamation-circle':
      return (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" {...rest}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M16 0.914286C7.66824 0.914286 0.914286 7.66824 0.914286 16C0.914286 24.3318 7.66824 31.0857 16 31.0857C24.3318 31.0857 31.0857 24.3318 31.0857 16C31.0857 7.66824 24.3318 0.914286 16 0.914286ZM0 16C0 7.1633 7.1633 0 16 0C24.8367 0 32 7.1633 32 16C32 24.8367 24.8367 32 16 32C7.1633 32 0 24.8367 0 16Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M16.4573 6.85718V18.7429H15.543V6.85718H16.4573Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M16.4573 22.4V25.1429H15.543V22.4H16.4573Z"
            fill={fillOrDefault}
          />
        </svg>
      )
    case 'information-circle':
      return (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...rest}>
          <mask
            id="iconInformationCircleMask0"
            mask-type="alpha"
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="20"
            height="20"
          >
            <path fillRule="evenodd" clipRule="evenodd" d="M0.5 0.5H19.8129V19.812H0.5V0.5Z" fill="white" />
          </mask>
          <g mask="url(#iconInformationCircleMask0)">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M10.157 0.5C4.832 0.5 0.5 4.832 0.5 10.156C0.5 15.481 4.832 19.812 10.157 19.812C15.481 19.812 19.813 15.481 19.813 10.156C19.813 4.832 15.481 0.5 10.157 0.5ZM10.157 1.5C14.937 1.5 18.813 5.375 18.813 10.156C18.813 14.937 14.937 18.812 10.157 18.812C5.376 18.812 1.5 14.937 1.5 10.156C1.5 5.375 5.376 1.5 10.157 1.5Z"
              fill={fillOrDefault}
            />
          </g>
          <mask
            id="iconInformationCircleMask1"
            mask-type="alpha"
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="20"
            height="20"
          >
            <path fillRule="evenodd" clipRule="evenodd" d="M0.5 19.8126H19.813V0.499634H0.5V19.8126Z" fill="white" />
          </mask>
          <g mask="url(#iconInformationCircleMask1)">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M9.67808 6.71668H11.1861V5.11768H9.67808V6.71668ZM7.45508 13.1397H9.82108V8.66768H7.71508V7.65268H11.0561V13.1397H13.3571V14.1537H7.45508V13.1397Z"
              fill={fillOrDefault}
            />
          </g>
        </svg>
      )
    case 'times':
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" {...rest}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M0 15.3719L15.3716 0.000244141L15.9997 0.62835L0.628106 16L0 15.3719Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M0.628106 0L15.9997 15.3716L15.3716 15.9997L0 0.628106L0.628106 0Z"
            fill={fillOrDefault}
          />
        </svg>
      )
    case 'external-link':
      return (
        <svg width="17" height="18" viewBox="0 0 17 18" fill="none" {...rest}>
          <path
            d="M16.7899 17.26H0.439941V0.900024H7.85994V1.90002H1.43994V16.26H15.7899V9.86002H16.7899V17.26Z"
            fill={fillOrDefault}
          />
          <path
            d="M11.0902 0.900024V1.90002H15.0902L8.74023 8.23002L9.45023 8.94002L15.7902 2.60002V6.60002H16.7902V0.900024H11.0902Z"
            fill={fillOrDefault}
          />
        </svg>
      )
    case 'spotify':
      return (
        <svg width="168" height="168" viewBox="0 0 168 168" {...rest}>
          <path
            fill={fillOrDefault}
            fillRule="evenodd"
            d="M133.28 74.24c-26.994-16.031-71.52-17.505-97.289-9.684-4.139 1.256-8.515-1.08-9.768-5.218a7.835 7.835 0 015.22-9.772c29.582-8.979 78.756-7.245 109.832 11.202a7.832 7.832 0 012.737 10.733c-2.208 3.722-7.019 4.949-10.732 2.739m-.884 23.744c-1.894 3.073-5.912 4.037-8.981 2.15-22.504-13.833-56.822-17.841-83.447-9.759a6.539 6.539 0 01-8.148-4.35 6.538 6.538 0 014.354-8.142c30.415-9.23 68.226-4.76 94.074 11.125 3.069 1.89 4.035 5.91 2.148 8.976m-10.247 22.803a5.215 5.215 0 01-7.177 1.737c-19.665-12.019-44.417-14.733-73.567-8.075a5.216 5.216 0 01-6.249-3.924 5.213 5.213 0 013.925-6.25c31.9-7.292 59.263-4.154 81.336 9.334a5.22 5.22 0 011.732 7.178M83.743 0C37.494 0 0 37.493 0 83.742c0 46.254 37.494 83.745 83.743 83.745 46.251 0 83.744-37.491 83.744-83.745C167.487 37.493 129.994 0 83.743 0"
          />
        </svg>
      )
    case 'check-circle':
      return (
        <svg width="34" height="34" viewBox="0 0 34 34" fill="none" {...rest}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M33 17C33 25.8367 25.8367 33 17 33C8.16329 33 1 25.8367 1 17C1 8.16329 8.16329 1 17 1C25.8367 1 33 8.16329 33 17Z"
            stroke={fillOrDefault}
          />
          <path d="M26.4831 10.8466L14.4173 22.9115L8.38525 16.8795" stroke={fillOrDefault} />
        </svg>
      )
    case 'appointment':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" {...rest}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M3 4.29187H20.999V20.6509H3V4.29187ZM4 5.29187V19.6509H19.999V5.29187H4Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M20.498 8.8158H3.18604V7.8158H20.498V8.8158Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M7.24951 4.47437V3.00037H8.24951V4.47437H7.24951Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M15.749 4.47437V3.00037H16.749V4.47437H15.749Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M6.74951 13.2611V11.2611H8.74951V13.2611H6.74951Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.999 13.2611V11.2611H12.999V13.2611H10.999Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M15.249 13.2611V11.2611H17.249V13.2611H15.249Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="nonzero"
            clipRule="evenodd"
            d="M6.74951 17.2064V15.2064H8.74951V17.2064H6.74951Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="nonzero"
            clipRule="evenodd"
            d="M10.999 17.2064V15.2064H12.999V17.2064H10.999Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M15.249 17.2064V15.2064H17.249V17.2064H15.249Z"
            fill={fillOrDefault}
          />
        </svg>
      )
    case 'power':
      return (
        <svg width="23" height="26" viewBox="0 0 23 26" fill="none" {...rest}>
          <path fillRule="evenodd" clipRule="evenodd" d="M11 11.9287V0.5H11.625V11.9287H11Z" fill={fillOrDefault} />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14.4262 3.81474C14.4259 3.81468 14.4264 3.8148 14.4262 3.81474C14.6522 3.88271 14.876 3.95806 15.0961 4.04018C17.3295 4.87366 19.2402 6.42509 20.5288 8.4664C21.9446 10.7091 22.515 13.3929 22.1354 16.0265C21.7559 18.6601 20.4518 21.0671 18.4621 22.8069C16.4724 24.5466 13.9302 25.5026 11.3009 25.4998C8.67173 25.497 6.13149 24.5356 4.14541 22.7916C2.15933 21.0476 0.860327 18.6377 0.486277 16.0034C0.112228 13.369 0.688166 10.6863 2.10862 8.44671C3.40156 6.40815 5.31543 4.86082 7.55057 4.03212C7.77102 3.95038 7.99459 3.87564 8.221 3.80811L8.43025 4.52691C6.07894 5.22826 4.05589 6.76353 2.73158 8.85156C1.40726 10.9396 0.870301 13.4407 1.21903 15.8967C1.56777 18.3528 2.77885 20.5996 4.6305 22.2255C6.48216 23.8515 8.85046 24.7478 11.3017 24.7505C13.753 24.7531 16.1232 23.8618 17.9782 22.2398C19.8333 20.6178 21.049 18.3737 21.4029 15.9183C21.7567 13.463 21.225 10.9608 19.905 8.86992C18.5855 6.77968 16.5664 5.24039 14.2175 4.53373C14.2168 4.53352 14.2161 4.53331 14.2154 4.5331L14.4262 3.81474Z"
            fill={fillOrDefault}
          />
        </svg>
      )
    case 'complete-cycle':
      return (
        <svg width="29" height="31" viewBox="0 0 29 31" fill="none" {...rest}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M0.66626 17.3553C0.66626 13.4584 2.38275 9.97061 5.08581 7.5791L5.638 8.20323C3.10446 10.4448 1.49959 13.7092 1.49959 17.3553C1.49959 24.1085 6.97452 29.5834 13.7277 29.5834C20.4808 29.5834 25.9557 24.1085 25.9557 17.3553C25.9557 13.7092 24.3517 10.4447 21.8182 8.20323L22.3704 7.5791C25.0735 9.97066 26.7891 13.4585 26.7891 17.3553C26.7891 24.5687 20.9411 30.4167 13.7277 30.4167C6.51428 30.4167 0.66626 24.5687 0.66626 17.3553Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M21.5835 7.47461H28.0637V8.30794H22.4168V13.954H21.5835V7.47461Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12.9683 8.19778L20.1917 0.833008L20.6667 1.31729L12.9683 9.16634L9 5.1204L9.47498 4.63612L12.9683 8.19778Z"
            fill={fillOrDefault}
          />
        </svg>
      )
    case 'exclamation-triangle':
      return (
        <svg width="32" height="28" viewBox="0 0 32 28" fill="none" {...rest}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M19.9718 2.90586C18.2296 -0.0792979 13.7704 -0.0792979 12.0282 2.90586L1.59187 20.7881C-0.116861 23.716 2.07833 27.3337 5.56368 27.3337H26.4363C29.9217 27.3337 32.1169 23.716 30.4081 20.7881L19.9718 2.90586Z"
            stroke={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M16.4166 8.16699V18.167H15.5833V8.16699H16.4166Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M16.4166 20.667V23.167H15.5833V20.667H16.4166Z"
            fill={fillOrDefault}
          />
        </svg>
      )
    case 'late':
      return (
        <svg width="31" height="31" viewBox="0 0 31 31" fill="none" {...rest}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M15.4915 1C7.48765 1 1.00049 7.48812 1.00049 15.491C1.00049 23.4939 7.48765 29.982 15.4915 29.982C23.4943 29.982 29.9825 23.4939 29.9825 15.491C29.9825 7.48814 23.4943 1 15.4915 1ZM0.000488281 15.491C0.000488281 6.93588 6.93532 0 15.4915 0C24.0466 0 30.9825 6.93586 30.9825 15.491C30.9825 24.0461 24.0466 30.982 15.4915 30.982C6.93532 30.982 0.000488281 24.0461 0.000488281 15.491Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14.9912 5.40747H15.9912V15.7755L25.1208 22.0954L24.5516 22.9176L14.9912 16.2995V5.40747Z"
            fill={fillOrDefault}
          />
        </svg>
      )
    case 'add-item':
      return (
        <svg width="18" height="19" viewBox="0 0 18 19" {...rest}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M9 3.875C5.89334 3.875 3.375 6.39334 3.375 9.5C3.375 12.6067 5.89334 15.125 9 15.125C12.1067 15.125 14.625 12.6067 14.625 9.5C14.625 6.39334 12.1067 3.875 9 3.875ZM2.625 9.5C2.625 5.97913 5.47913 3.125 9 3.125C12.5209 3.125 15.375 5.97913 15.375 9.5C15.375 13.0209 12.5209 15.875 9 15.875C5.47913 15.875 2.625 13.0209 2.625 9.5Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8.80151 12.8529V6.5H9.55151V12.8529H8.80151Z"
            fill={fillOrDefault}
          />
          <path fillRule="evenodd" clipRule="evenodd" d="M6 9.30127H12.3529V10.0513H6V9.30127Z" fill={fillOrDefault} />
        </svg>
      )
    case 'happy-face':
      return (
        <svg width="48" height="48" viewBox="0 0 48 48" fill={fillOrDefault} {...rest}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.6317 4.14535C13.2424 2.36766 16.179 1.1251 19.2718 0.489323C22.3647 -0.14645 25.5527 -0.162844 28.6519 0.441085C31.7511 1.04501 34.7002 2.25732 37.3291 4.00806C39.958 5.7588 42.2146 8.01333 43.9688 10.6416C45.723 13.2699 46.9401 16.2199 47.5498 19.3214C48.1595 22.423 48.1498 25.6147 47.5213 28.7125C46.8927 31.8102 45.6577 34.7528 43.8876 37.3703C42.1174 39.9878 39.8471 42.2285 37.2076 43.9632C31.9336 47.492 25.4754 48.7786 19.2537 47.5402C13.0321 46.3017 7.55673 42.6396 4.03208 37.3594C0.507439 32.0792 -0.777736 25.6135 0.459283 19.3847C1.6963 13.1558 5.35419 7.67409 10.6282 4.14535H10.6317ZM12.5314 41.1206C14.7696 42.6347 17.2847 43.691 19.932 44.2288C22.5793 44.7666 25.3066 44.7754 27.9573 44.2545C30.608 43.7337 33.1298 42.6935 35.3776 41.1938C37.6255 39.6941 39.5552 37.7644 41.0557 35.5157C42.5563 33.267 43.5982 30.7435 44.1215 28.0903C44.6448 25.4372 44.6393 22.7066 44.1051 20.0556C43.571 17.4046 42.5188 14.8855 41.0091 12.6429C39.4994 10.4003 37.5618 8.47852 35.3079 6.98802C30.7866 3.99818 25.2668 2.92197 19.9553 3.99465C14.6437 5.06733 9.97236 8.20165 6.96232 12.7125C3.95228 17.2233 2.84839 22.7437 3.89196 28.067C4.93552 33.3903 8.04167 38.0834 12.5314 41.1206ZM18.7944 20.6335C18.7944 21.5408 18.4344 22.4108 17.7937 23.0524C17.1529 23.6939 16.2838 24.0543 15.3776 24.0543C14.4714 24.0543 13.6023 23.6939 12.9616 23.0524C12.3208 22.4108 11.9608 21.5408 11.9608 20.6335C11.9608 19.7263 12.3208 18.8562 12.9616 18.2147C13.6023 17.5731 14.4714 17.2127 15.3776 17.2127C16.2838 17.2127 17.1529 17.5731 17.7937 18.2147C18.4344 18.8562 18.7944 19.7263 18.7944 20.6335ZM35.8785 20.6335C35.8785 21.5408 35.5185 22.4108 34.8777 23.0524C34.2369 23.6939 33.3679 24.0543 32.4617 24.0543C31.5555 24.0543 30.6864 23.6939 30.0456 23.0524C29.4048 22.4108 29.0449 21.5408 29.0449 20.6335C29.0449 19.7263 29.4048 18.8562 30.0456 18.2147C30.6864 17.5731 31.5555 17.2127 32.4617 17.2127C33.3679 17.2127 34.2369 17.5731 34.8777 18.2147C35.5185 18.8562 35.8785 19.7263 35.8785 20.6335ZM23.9196 34.3166C22.063 34.3212 20.2399 33.8208 18.6452 32.8689C17.0504 31.9169 15.7439 30.5492 14.8651 28.9118L11.8925 30.5538C13.0888 32.7703 14.8733 34.6131 17.0491 35.8791C19.2249 37.1451 21.7076 37.785 24.2235 37.7284C26.7394 37.6718 29.1909 36.9208 31.3077 35.5583C33.4245 34.1958 35.1244 32.2746 36.2202 30.0065L33.145 28.5355C32.3046 30.2691 30.9935 31.7307 29.362 32.7531C27.7305 33.7755 25.8444 34.3174 23.9196 34.3166Z"
            fill={fillOrDefault}
          />
        </svg>
      )
    case 'instructions':
      return (
        <svg height="24px" viewBox="0 0 24 24" width="24px" fill="#e8eaed">
          <path d="M0 0h24v24H0V0z" fill="none" />
          <path d="M12 3 1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z" />
        </svg>
      )
    case 'instructions-reverse':
      return (
        <svg height="24px" viewBox="0 0 24 24" width="24px" fill="#FFFFFF">
          <path d="M0 0h24v24H0z" fill="none" />
          <path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3 1 9l11 6 9-4.91V17h2V9L12 3z" />
        </svg>
      )
    case 'checklist':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...rest}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M15.7323 10.7981L20.457 6.07336L20.8105 6.42692L15.7323 11.5052L13.1045 8.87742L13.458 8.52386L15.7323 10.7981Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M16.25 18.7501H8.25V18.2501H16.25V18.7501Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M16.25 13.7501L8.25 13.7501V13.2501L16.25 13.2501V13.7501Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M16.25 16.2501H8.25V15.7501L16.25 15.7501V16.2501Z"
            fill={fillOrDefault}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M6 3.50012L18.5 3.50012V8.50012H18V4.00012L6.5 4.00012L6.5 20.5001H18V10.0001H18.5V21.0001H6L6 3.50012Z"
            fill={fillOrDefault}
          />
        </svg>
      )
    default:
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" {...props}>
          <path d="M4 19.3719L19.3716 4.00024L19.9997 4.62835L4.62811 20L4 19.3719Z" />
          <path d="M4.62811 4L19.9997 19.3716L19.3716 19.9997L4 4.62811L4.62811 4Z" />
        </svg>
      )
  }
}
